/**
 * Aviation vertical: 4-step adaptive wizard + profile_targeting_json contract.
 * Keep in sync with server `app/constants/aviation_profile_targeting.py`.
 */

import type { AviationTaxonomyResponse } from "@services/aviationTaxonomy";

export const WIZARD_SCHEMA_VERSION = "2026_4step_v2";

export type PrimaryActivityFamily =
  | "aircraft_operations"
  | "airport_airfield"
  | "maintenance_repair"
  | "design_production"
  | "training"
  | "uas_aam"
  | "specialized_flight"
  | "security_cargo_hazmat"
  | "dot_seller_arranger"
  | "atc_navigation"
  | "commercial_space"
  | "not_sure";

/** Screen 1 — exact UX framing */
export const PRIMARY_ACTIVITY_FAMILY_OPTIONS: readonly {
  value: PrimaryActivityFamily;
  label: string;
}[] = [
  { value: "aircraft_operations", label: "Aircraft operations" },
  { value: "airport_airfield", label: "Airport / airfield / heliport / vertiport operations" },
  { value: "maintenance_repair", label: "Maintenance / repair / alteration" },
  { value: "design_production", label: "Design / production / manufacturing / approval holder" },
  { value: "training", label: "Aviation training organization" },
  { value: "uas_aam", label: "UAS / drone / AAM operations" },
  { value: "specialized_flight", label: "Specialized flight operations" },
  { value: "security_cargo_hazmat", label: "Security / cargo / hazmat regulated activity" },
  { value: "dot_seller_arranger", label: "DOT public charter / ticketing / arranger / consumer aviation business" },
  { value: "atc_navigation", label: "Air navigation / ATC / non-federal navigation facility" },
  { value: "commercial_space", label: "Commercial space launch / reentry / site operator" },
  { value: "not_sure", label: "Not sure — guide me with questions" },
];

/** Short line shown under each label; `detail` appears on hover (Screen 1). */
export type PrimaryActivityFamilyBlurb = { summary: string; detail: string };

export const PRIMARY_ACTIVITY_FAMILY_BLURBS: Record<
  PrimaryActivityFamily,
  PrimaryActivityFamilyBlurb
> = {
  aircraft_operations: {
    summary: "Carriers, charters, tours, fractional—flying for hire under FAA rules.",
    detail:
      "Scheduled and charter carriers (Parts 121/135), on‑demand ops, air tours, fractional programs, and related certificate / airworthiness obligations when you operate aircraft commercially. Hover for this fuller picture — Step 1 sets the theme; Step 2 will ask which operating certificate fits.",
  },
  airport_airfield: {
    summary: "Runways, ramps, sponsors & certificates—not “just a building.”",
    detail:
      "Part 139 certificated airports, public‑use airports, heliports & vertiports, sponsor / grant‑assurance roles, and regulated movement areas (runway / taxi / ramp). If you hold an Airport Operating Certificate, that’s Part 139 — ACM, movement-area safety, ARFF index, self-inspections, and FAA inspections.",
  },
  maintenance_repair: {
    summary: "Repair stations & approved maintenance—fixing and altering aircraft.",
    detail:
      "Preventive maintenance and alteration of aircraft, engines, propellers, or articles—typically under Part 145 repair stations or other FAA‑approved maintenance programs. Step 2 will distinguish standalone Part 145 from maintenance done under an air carrier’s program.",
  },
  design_production: {
    summary: "Designing & building what flies—type certs, STCs, PMA, production.",
    detail:
      "Type certificates, STCs, PMA, TSO authorization, production approvals, and certification support (ODA / DER / DAR) under Part 21. Each approval type has different FAA interfaces — we’ll ask which lane in Step 2.",
  },
  training: {
    summary: "Flight schools, instructors & checking—the training ecosystem.",
    detail:
      "Pilot and crew training, checking organizations, and syllabi governed by FAA training and operating rules—not casual hobby flying. Includes manned instruction vs UAS remote-pilot training contexts.",
  },
  uas_aam: {
    summary: "Drones, remote ID, waivers—and tomorrow’s air taxis (AAM).",
    detail:
      "Part 107 operations, Part 89 remote ID, waivers / exemptions, and emerging unmanned or advanced air mobility concepts. Step 2 narrows routine Part 107 vs waiver-driven vs Remote-ID-heavy programs.",
  },
  specialized_flight: {
    summary: "Crop dusting, external loads, specialty tours—non‑airline flying.",
    detail:
      "Examples: agricultural (Part 137), rotorcraft external load (Part 133), air tours (Part 136), parachute / drop‑zone contexts—not typical airline service. Pick the specialized part that matches how you earn revenue.",
  },
  security_cargo_hazmat: {
    summary: "Airport & operator security programs + hazmat moved by air.",
    detail:
      "TSA security programs (e.g. 1542 / 1544), hazmat transported by air, cargo screening, and related dangerous‑goods obligations. Step 2 picks whether airport security, operator security, hazmat, or cargo-chain risk leads.",
  },
  dot_seller_arranger: {
    summary: "Selling seats & charters—DOT economics & consumer protection.",
    detail:
      "Public charters (Part 380), brokers, ticket agents, advertising, and DOT filing / consumer rules when you sell or arrange air transportation — overlapping but distinct from FAA operating certificates.",
  },
  atc_navigation: {
    summary: "Keeping aircraft separated—ATC, nav aids & regulated facilities.",
    detail:
      "Air traffic control services, navigation aids, and facilities under FAA oversight—including certain non‑federal navigation sites. Detailed profile options are still expanding in our packs — baseline selection keeps you moving.",
  },
  commercial_space: {
    summary: "Launch, reentry & licensed sites—rocket side of FAA oversight.",
    detail:
      "Commercial space launch and reentry licensees and site operators under FAA commercial space transportation regulations. Richer launch vs reentry profiles are landing in our system — placeholder keeps onboarding unblocked.",
  },
  not_sure: {
    summary: "No worries—we’ll nudge you with a few easy questions first.",
    detail:
      "Short plain‑English prompts help narrow whether FAA, TSA, or DOT rules apply before you choose a detailed regulatory profile. Step 2 starts from a safe baseline until we learn more together.",
  },
};

/** Step 1 is satisfied when primary is chosen and/or at least one additional function is checked */
export function hasAviationStep1Selection(
  primary_activity_family: PrimaryActivityFamily | "",
  additional_activity_families: readonly PrimaryActivityFamily[],
): boolean {
  return Boolean(primary_activity_family) || additional_activity_families.length > 0;
}

/** Screen 2 regulatory options when primary dropdown was left blank but additional roles were checked */
export function effectivePrimaryActivityFamilyForWizard(
  primary_activity_family: PrimaryActivityFamily | "",
  additional_activity_families: readonly PrimaryActivityFamily[],
): PrimaryActivityFamily {
  if (primary_activity_family) return primary_activity_family;
  if (additional_activity_families.length > 0) return additional_activity_families[0];
  return "not_sure";
}

/**
 * Screen 2 — explicit, friendly explanation per Step 1 choice.
 * Displayed under the authority-profile title so users know *why* this step matters.
 */
export const AUTHORITY_PROFILE_STEP_DESCRIPTION_BY_FAMILY: Record<PrimaryActivityFamily, string> = {
  aircraft_operations: `You told us you’re in the flying-for-hire world—nice work, that’s where rules get very real!

Here’s what we’re asking now: which operating “hat” you wear under FAA rules. Scheduled airline (Part 121), charter or on-demand (Part 135), general/business (Part 91), fractional (91K), large-aircraft program (125), foreign carrier into the U.S. (129), or commercial air tour (136) each comes with different manuals, OpSpecs where applicable, and enforcement patterns.

Pick every operating “hat” that applies under FAA rules. Don’t stress about perfection—you can refine later; we just want the right regulatory lanes so UniComply can cheer you on with the right reminders.`,

  airport_airfield: `Airports and landing sites are more than asphalt—we love that you picked this lane!

Screen 2 is about your regulatory role at the airport or site: Part 139 certificated airport, public-use without a Part 139 certificate, airport sponsor / grant-assurance obligations, tenant / FBO, fueling, ARFF, or heliport / vertiport-style operations. Each path pulls different CFR chapters, inspection habits, and calendar risks.

Select every identity that fits who holds certificates or obligations for the entity we’re setting up — many airports wear more than one hat. That way we can celebrate the right compliance story with you—not a generic building checklist.`,

  maintenance_repair: `Maintainers and repair pros keep aviation safe—thanks for choosing maintenance / repair / alteration!

This step matters because “MRO” isn’t one-size-fits-all in the regulations. We’re asking whether your compliance center of gravity is a Part 145 repair station certificate (domestic or foreign), or maintenance performed under an air carrier’s Part 121/135 world (often via their program and OpSpecs, not a standalone 145 station).

If you hold your own Part 145 certificate, lean toward the domestic or foreign repair station option. If you’re mainly doing line or heavy work under an airline’s or charter operator’s certificate, “air carrier internal maintenance” is often the better fit. Pick what matches your FAA oversight story—we’re here to support whichever hat you actually wear!`,

  design_production: `Design, production, and manufacturing are where Part 21 shines—you’re building the future of certified hardware!

Each dropdown option maps to a different Part 21 “lane”: type certification, STCs, PMA articles, TSO authorization, production approval, or ODA/DER/DAR-style certification support. They’re not interchangeable—pick the one that matches the approval or production role your organization actually holds (or is pursuing).

Getting this right helps UniComply line up the right standards, change tracking, and rule updates with a smile instead of a scramble.`,

  training: `Training organizations keep pilots and crews sharp—we’re glad you’re here!

Screen 2 splits two common compliance stories: traditional manned-aircraft training and checking in a Parts 61/91-style context, versus training where Part 107 / UAS is the headline (e.g., remote pilot pathways). Choose the one that matches where most of your regulatory attention lives day to day.

If you straddle both, pick the primary one now—you can always broaden scope later. We’re rooting for clear, stress-free targeting!`,

  uas_aam: `Drones and advanced air mobility are fast-moving and fun to support—great choice!

We’re asking how you usually operate under FAA rules: routine Part 107, heavier waiver/BVLOS-style operations, or a Remote ID–heavy program (Part 89). Each mode has different priorities: pilot rules, airspace, equipage, and enforcement patterns.

Pick the profile that matches your typical operation today—you’re not locked in forever, but starting cheerful and accurate helps us tune alerts and resources for you.`,

  specialized_flight: `Specialized flying—ag, external load, jump ops—adds color to the sky and specific rules on the ground!

Each option ties to a different operational part: agricultural (137), rotorcraft external load (133), or parachute / drop-zone contexts leaning on Parts 91/105. They’re not interchangeable—pick the one that matches how you conduct those flights commercially.

We’ll use your answer to keep reminders and risk signals aligned with the way you actually fly. You’ve got this!`,

  security_cargo_hazmat: `Security and hazmat touch some of the highest-consequence rules—we’re happy to help you stay ahead.

Screen 2 picks which program leads: airport security (TSA Part 1542), aircraft operator security (1544), hazmat / dangerous goods in air transport, or cargo-handling / ground security. The right choice depends on whether your main headache is the airport program, the operator program, hazmat rules, or cargo chain security.

Choose the profile that matches where your certificate or program lives—we’ll align monitoring so you feel supported, not overwhelmed.`,

  dot_seller_arranger: `Selling seats, charters, or arranging trips brings DOT economics and consumer rules into play—thanks for being upfront about it!

We’re asking which economic “hat” fits: public charter under Part 380, charter broker / arranger, ticket-agent / advertising rules, or broader DOT consumer-protection buckets. They carry different filing, disclosure, and enforcement habits.

Pick what matches how you sell or arrange air transportation—we’ll tune UniComply so the cheerfully boring compliance stuff lands in the right inbox.`,

  atc_navigation: `Air navigation and ATC-related roles keep the NAS orderly—thanks for choosing this path!

Heads-up: our detailed regulatory profiles for this lane are still getting wired to every backend feed. For now, choose the placeholder baseline so we can still anchor activity domains and lookup—and expect richer certificate-specific options soon.

You’re not forgotten—we’re building this one out with care.`,

  commercial_space: `Commercial space is exhilarating—and the FAA commercial space rules are their own universe!

Note: launch/reentry and site-operator profiles are still rolling into our full pack list. Please pick the placeholder baseline for now so onboarding doesn’t block; we’ll layer in launch vs reentry vs site roles as feeds land.

Thanks for your patience—we’re excited to grow this with you.`,

  not_sure: `No problem at all—“not sure” is a perfectly fine place to start!

Because we don’t know your main lane yet, pick the general baseline below (or the closest guess). That keeps UniComply useful while your Step 1 triage answers or your team narrows the real certificate.

You’re doing great—once we learn more, we can tighten the profile together and turn up the helpful reminders that actually match your world.`,
};

export function getAuthorityProfileStepDescription(
  primary_activity_family: PrimaryActivityFamily | "",
  additional_activity_families: readonly PrimaryActivityFamily[],
): string {
  const fam = effectivePrimaryActivityFamilyForWizard(primary_activity_family, additional_activity_families);
  return AUTHORITY_PROFILE_STEP_DESCRIPTION_BY_FAMILY[fam];
}

/** Default primary_profile_slug when Screen 2 does not override (matches Python). */
export const DEFAULT_PROFILE_SLUG_BY_FAMILY: Record<PrimaryActivityFamily, string> = {
  aircraft_operations: "aviation_air_operator_part_135",
  airport_airfield: "aviation_airport_part_139",
  maintenance_repair: "aviation_repair_station_part_145",
  design_production: "aviation_production_approval_part_21",
  training: "aviation_general_operations_part_91",
  uas_aam: "aviation_uas_part_107",
  specialized_flight: "aviation_agricultural_part_137",
  security_cargo_hazmat: "aviation_hazmat_air_transport",
  dot_seller_arranger: "aviation_public_charter_part_380",
  atc_navigation: "aviation_general_operations_part_91",
  commercial_space: "aviation_general_operations_part_91",
  not_sure: "aviation_general_operations_part_91",
};

export type RefinedProfileOption = readonly [slug: string, label: string];

/** Screen 2 — regulatory identity / certificate profile (must align with Rules API slugs). */
export const REFINED_PROFILE_OPTIONS_BY_FAMILY: Record<PrimaryActivityFamily, readonly RefinedProfileOption[]> = {
  aircraft_operations: [
    ["aviation_air_carrier_part_121", "Scheduled airline / Part 121 air carrier"],
    ["aviation_air_operator_part_135", "Charter / on-demand / commuter (Part 135)"],
    ["aviation_general_operations_part_91", "General or business aviation (Part 91)"],
    ["aviation_fractional_part_91k", "Fractional program (Part 91 subpart K)"],
    ["aviation_large_civil_aircraft_part_125", "Large civil aircraft (Part 125)"],
    ["aviation_foreign_air_carrier_part_129", "Foreign air carrier into the U.S. (Part 129)"],
    ["aviation_air_tour_part_136", "Commercial air tour (Part 136)"],
  ],
  airport_airfield: [
    ["aviation_airport_part_139", "Certificated airport (Part 139)"],
    ["aviation_airport_public_use_non_139", "Public-use airport (non–Part 139)"],
    ["aviation_airport_sponsor_grant_assurance", "Airport sponsor / grant assurance"],
    ["aviation_airport_tenant_fbo", "Airport tenant / FBO"],
    ["aviation_airport_fueling_ops", "Airport fueling operations"],
    ["aviation_airport_arff", "ARFF provider"],
    ["aviation_vertiport_heliport_operations", "Heliport / vertiport operations"],
  ],
  maintenance_repair: [
    ["aviation_repair_station_part_145", "Domestic repair station (Part 145)"],
    ["aviation_foreign_repair_station_part_145", "Foreign repair station (Part 145)"],
    ["aviation_air_carrier_maintenance_121_135", "Air carrier internal maintenance"],
  ],
  design_production: [
    ["aviation_type_certification_part_21", "Type certification (Part 21)"],
    ["aviation_stc_part_21_subpart_e", "Supplemental type certificate (STC)"],
    ["aviation_pma_part_21", "PMA / replacement or modification article"],
    ["aviation_tso_authorization_part_21", "TSO authorization"],
    ["aviation_production_approval_part_21", "Production approval (PC)"],
    ["aviation_oda_der_dar_certification_support", "ODA / DER / DAR / certification support"],
  ],
  training: [
    ["aviation_general_operations_part_91", "Flight training / instruction (Parts 61/91 context)"],
    ["aviation_uas_part_107", "Remote pilot / UAS training context"],
  ],
  uas_aam: [
    ["aviation_uas_part_107", "Small UAS (Part 107)"],
    ["aviation_uas_bvlos_waiver_exemption", "BVLOS / waiver-driven operations"],
    ["aviation_uas_remote_id_part_89", "Remote ID (Part 89)"],
  ],
  specialized_flight: [
    ["aviation_agricultural_part_137", "Agricultural aircraft (Part 137)"],
    ["aviation_external_load_part_133", "Rotorcraft external load (Part 133)"],
    ["aviation_general_operations_part_91", "Parachute / drop zone (Parts 91/105 context)"],
  ],
  security_cargo_hazmat: [
    ["aviation_airport_security_tsa_1542", "Airport security program (TSA Part 1542)"],
    ["aviation_aircraft_operator_security_tsa_1544", "Aircraft operator security (TSA Part 1544)"],
    ["aviation_hazmat_air_transport", "Hazmat / dangerous goods air transport"],
    ["aviation_cargo_handling_security", "Cargo handling / ground security"],
  ],
  dot_seller_arranger: [
    ["aviation_public_charter_part_380", "Public charter operator / Part 380"],
    ["aviation_air_charter_broker", "Air charter broker / arranger"],
    ["aviation_ticket_agent_advertising", "Ticket agent / advertising"],
    ["aviation_dot_consumer_protection", "DOT consumer protection"],
  ],
  atc_navigation: [
    ["aviation_general_operations_part_91", "ATC / navigation context (placeholder slug)"],
  ],
  commercial_space: [
    ["aviation_general_operations_part_91", "Commercial space (placeholder slug — feeds pending)"],
  ],
  not_sure: [
    ["aviation_general_operations_part_91", "General aviation baseline while triage completes"],
  ],
};

/** Maps Screen 1 families to legacy activity_domains keys (API + lookup). */
export const FAMILY_TO_ACTIVITY_DOMAINS: Record<PrimaryActivityFamily, string[]> = {
  aircraft_operations: ["aircraft_operations"],
  airport_airfield: ["airport_operations"],
  maintenance_repair: ["maintenance_repair_alteration"],
  design_production: ["design_certification_manufacturing"],
  training: ["training_organization"],
  uas_aam: ["uas_aam"],
  specialized_flight: ["specialized_flight_operations"],
  security_cargo_hazmat: ["security", "hazmat_cargo"],
  dot_seller_arranger: ["economic_consumer_arranger"],
  atc_navigation: ["atc_navigation"],
  commercial_space: ["commercial_space_operations"],
  not_sure: [],
};

export const AVIATION_PRIMARY_DOMAIN_BY_PROFILE: Record<string, string> = {
  aviation_air_carrier_part_121: "aircraft_operations",
  aviation_air_operator_part_135: "aircraft_operations",
  aviation_general_operations_part_91: "aircraft_operations",
  aviation_fractional_part_91k: "aircraft_operations",
  aviation_large_civil_aircraft_part_125: "aircraft_operations",
  aviation_foreign_air_carrier_part_129: "aircraft_operations",
  aviation_air_tour_part_136: "aircraft_operations",
  aviation_airport_part_139: "airport_operations",
  aviation_airport_public_use_non_139: "airport_operations",
  aviation_airport_sponsor_grant_assurance: "airport_operations",
  aviation_vertiport_heliport_operations: "airport_operations",
  aviation_repair_station_part_145: "maintenance_repair_alteration",
  aviation_foreign_repair_station_part_145: "maintenance_repair_alteration",
  aviation_air_carrier_maintenance_121_135: "maintenance_repair_alteration",
  aviation_type_certification_part_21: "design_certification_manufacturing",
  aviation_stc_part_21_subpart_e: "design_certification_manufacturing",
  aviation_pma_part_21: "design_certification_manufacturing",
  aviation_tso_authorization_part_21: "design_certification_manufacturing",
  aviation_production_approval_part_21: "design_certification_manufacturing",
  aviation_oda_der_dar_certification_support: "design_certification_manufacturing",
  aviation_uas_part_107: "uas_aam",
  aviation_uas_bvlos_waiver_exemption: "uas_aam",
  aviation_uas_remote_id_part_89: "uas_aam",
  aviation_agricultural_part_137: "specialized_flight_operations",
  aviation_external_load_part_133: "specialized_flight_operations",
  aviation_airport_security_tsa_1542: "security",
  aviation_aircraft_operator_security_tsa_1544: "security",
  aviation_hazmat_air_transport: "hazmat_cargo",
  aviation_cargo_handling_security: "hazmat_cargo",
  aviation_public_charter_part_380: "economic_consumer_arranger",
  aviation_air_charter_broker: "economic_consumer_arranger",
  aviation_ticket_agent_advertising: "economic_consumer_arranger",
  aviation_dot_consumer_protection: "economic_consumer_arranger",
};

export interface AviationFourStepWizardState {
  primary_activity_family: PrimaryActivityFamily | "";
  additional_activity_families: PrimaryActivityFamily[];
  /** First selected regulatory identity (API primary). */
  primary_profile_slug: string;
  /** Extra Screen 2 identities when the entity wears more than one certificate hat. */
  additional_profile_slugs: string[];
  /** Legacy field; wizard no longer collects roles separately — kept for API parity. */
  legal_roles: string[];
  activity_domains: string[];
  operation_contexts: string[];
  asset_types: string[];
  certificates_or_approvals: string[];
  risk_targets: string[];
  regulation_packs: string[];
  overlay_packs: string[];
  penalty_packs: string[];
  wizard_schema_version: string;
  /** When Screen 1 is "not_sure" */
  not_sure_triage?: {
    operate?: string;
    hold_certificate?: string;
    assets?: string;
  };
}

export const DEFAULT_AVIATION_FOUR_STEP: AviationFourStepWizardState = {
  primary_activity_family: "",
  additional_activity_families: [],
  primary_profile_slug: "",
  additional_profile_slugs: [],
  legal_roles: [],
  activity_domains: [],
  operation_contexts: [],
  asset_types: [],
  certificates_or_approvals: [],
  risk_targets: [
    "civil_penalties",
    "certificate_action",
    "failed_audit",
    "ad_noncompliance",
    "operating_without_authority",
    "recordkeeping_violation",
    "missed_training",
    "consumer_complaint",
    "tsa_security_violation",
    "nprm_rule_monitoring",
    "final_rule_cfr_amendment",
    "manual_program_amendment",
    "faa_order_ac_saib_watch",
  ],
  regulation_packs: [],
  overlay_packs: [],
  penalty_packs: [],
  wizard_schema_version: WIZARD_SCHEMA_VERSION,
};

export function activityDomainsFromFamilies(
  primary: PrimaryActivityFamily | "",
  additional: PrimaryActivityFamily[],
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const pushFam = (fam: PrimaryActivityFamily) => {
    for (const d of FAMILY_TO_ACTIVITY_DOMAINS[fam] ?? []) {
      if (!seen.has(d)) {
        seen.add(d);
        out.push(d);
      }
    }
  };
  if (primary) pushFam(primary);
  for (const f of additional) {
    if (f !== primary) pushFam(f);
  }
  return out;
}

export function getAviationPrimaryDomain(profileSlug: string): string | null {
  return AVIATION_PRIMARY_DOMAIN_BY_PROFILE[profileSlug] ?? null;
}

/** Ordered selected regulatory identities (primary first, then additional). */
export function selectedProfileSlugs(
  state: Pick<AviationFourStepWizardState, "primary_profile_slug" | "additional_profile_slugs">,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (slug: string | undefined) => {
    const key = (slug || "").trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(key);
  };
  push(state.primary_profile_slug);
  for (const slug of state.additional_profile_slugs ?? []) push(slug);
  return out;
}

/** Toggle a Screen 2 identity; first remaining selection becomes primary. */
export function toggleProfileSlugSelection(
  state: AviationFourStepWizardState,
  slug: string,
): AviationFourStepWizardState {
  const key = slug.trim();
  if (!key) return state;
  const current = selectedProfileSlugs(state);
  const next = current.includes(key)
    ? current.filter((s) => s !== key)
    : [...current, key];
  return {
    ...state,
    primary_profile_slug: next[0] || "",
    additional_profile_slugs: next.slice(1),
  };
}

export function mergeDomainsFromProfileAndFamilies(
  profileSlug: string,
  primaryFamily: PrimaryActivityFamily | "",
  additionalFamilies: PrimaryActivityFamily[],
  explicitDomains: string[],
  additionalProfileSlugs: string[] = [],
): string[] {
  const fromFamilies = activityDomainsFromFamilies(primaryFamily, additionalFamilies);
  const merged: string[] = [];
  const seen = new Set<string>();
  const push = (d: string) => {
    const k = d.trim();
    if (!k || seen.has(k)) return;
    seen.add(k);
    merged.push(k);
  };
  for (const d of explicitDomains) push(d);
  for (const slug of [profileSlug, ...additionalProfileSlugs]) {
    const inferred = getAviationPrimaryDomain(slug);
    if (inferred) push(inferred);
  }
  for (const d of fromFamilies) push(d);
  return merged;
}

/** Normalize wizard state into API aviation_targeting payload (plus legacy arrays). */
export function buildAviationTargetingPayload(
  state: AviationFourStepWizardState,
): Record<string, unknown> {
  const extraProfiles = state.additional_profile_slugs ?? [];
  const mergedDomains = mergeDomainsFromProfileAndFamilies(
    state.primary_profile_slug,
    state.primary_activity_family,
    state.additional_activity_families,
    state.activity_domains,
    extraProfiles,
  );
  const packs = derivePacksFromWizardState(state);
  return {
    wizard_schema_version: WIZARD_SCHEMA_VERSION,
    primary_activity_family: state.primary_activity_family || undefined,
    additional_activity_families:
      state.additional_activity_families.length > 0 ? state.additional_activity_families : undefined,
    primary_profile_slug: state.primary_profile_slug,
    additional_profile_slugs: extraProfiles.length > 0 ? extraProfiles : undefined,
    activity_domains: mergedDomains,
    legal_roles: state.legal_roles,
    operation_contexts: state.operation_contexts,
    asset_types: state.asset_types,
    certificates_or_approvals: state.certificates_or_approvals,
    risk_targets: state.risk_targets,
    regulation_packs: packs.regulation_packs.length > 0 ? packs.regulation_packs : state.regulation_packs,
    overlay_packs: packs.overlay_packs.length > 0 ? packs.overlay_packs : state.overlay_packs,
    penalty_packs: packs.penalty_packs.length > 0 ? packs.penalty_packs : state.penalty_packs,
    not_sure_triage: state.not_sure_triage,
  };
}

/** Client-side pack hints from profile slug (server resolver is authoritative on save). */
export function derivePacksFromWizardState(
  state: AviationFourStepWizardState,
  taxonomy?: {
    profiles_by_family?: Record<
      string,
      ReadonlyArray<{ value: string; regulation_packs?: string[]; overlay_packs?: string[]; penalty_packs?: string[] }>
    >;
  } | null,
): { regulation_packs: string[]; overlay_packs: string[]; penalty_packs: string[] } {
  const merge = (...groups: string[][]) => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const group of groups) {
      for (const item of group) {
        const key = item.trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(key);
      }
    }
    return out;
  };

  const family = effectivePrimaryActivityFamilyForWizard(
    state.primary_activity_family,
    state.additional_activity_families,
  );
  const profileNodes = taxonomy?.profiles_by_family?.[family] ?? [];
  const selectedSlugs = selectedProfileSlugs(state);
  const regulationFromProfiles: string[] = [];
  const overlayFromProfiles: string[] = [];
  const penaltyFromProfiles: string[] = [];
  for (const slug of selectedSlugs) {
    const profileNode = profileNodes.find((node) => node.value === slug);
    regulationFromProfiles.push(...(profileNode?.regulation_packs ?? []));
    overlayFromProfiles.push(...(profileNode?.overlay_packs ?? []));
    penaltyFromProfiles.push(...(profileNode?.penalty_packs ?? []));
  }

  return {
    regulation_packs: merge(regulationFromProfiles, state.regulation_packs),
    overlay_packs: merge(overlayFromProfiles, state.overlay_packs),
    penalty_packs: merge(penaltyFromProfiles, state.penalty_packs),
  };
}

export type AviationTaxonomyLike = {
  wizard_schema_version: string;
  steps: AviationWizardStep[];
  families: ReadonlyArray<{ value: PrimaryActivityFamily; label: string; summary?: string; help_text?: string }>;
  profiles_by_family: Record<PrimaryActivityFamily, ReadonlyArray<readonly [string, string]>>;
  scope_options: {
    operation_contexts: ReadonlyArray<readonly [string, string]>;
    asset_types: ReadonlyArray<readonly [string, string]>;
    certificates_or_approvals: ReadonlyArray<readonly [string, string]>;
  };
  risk_targets: ReadonlyArray<readonly [string, string]>;
  default_profile_slug_by_family: Record<PrimaryActivityFamily, string>;
  family_to_activity_domains: Record<PrimaryActivityFamily, string[]>;
};

/** Convert Rules API taxonomy to wizard config shape. */
export function taxonomyResponseToWizardConfig(taxonomy: AviationTaxonomyResponse): AviationTaxonomyLike {
  const profilesByFamily = Object.fromEntries(
    Object.entries(taxonomy.profiles_by_family).map(([family, nodes]) => [
      family,
      nodes.map((node) => [node.value, node.label] as const),
    ]),
  ) as unknown as AviationTaxonomyLike["profiles_by_family"];

  const scopeFromNodes = (key: keyof AviationTaxonomyResponse["scope_options"]) =>
    (taxonomy.scope_options[key] ?? []).map(
      (node) => [node.value, node.label] as const,
    );

  return {
    wizard_schema_version: taxonomy.wizard_schema_version,
    steps: taxonomy.steps as AviationWizardStep[],
    families: taxonomy.families.map((node) => ({
      value: node.value as PrimaryActivityFamily,
      label: node.label,
      summary: node.summary,
      help_text: node.help_text,
    })),
    profiles_by_family: profilesByFamily,
    scope_options: {
      operation_contexts: scopeFromNodes("operation_contexts"),
      asset_types: scopeFromNodes("asset_types"),
      certificates_or_approvals: scopeFromNodes("certificates_or_approvals"),
    },
    risk_targets: taxonomy.risk_targets.map(
      (node) => [node.value, node.label] as const,
    ),
    default_profile_slug_by_family: taxonomy.default_profile_slug_by_family as Record<
      PrimaryActivityFamily,
      string
    >,
    family_to_activity_domains: taxonomy.family_to_activity_domains as Record<
      PrimaryActivityFamily,
      string[]
    >,
  };
}

/** Map Rules API taxonomy onto static wizard config shape for rendering. */
export function mapTaxonomyToWizardConfig(
  taxonomy: AviationTaxonomyLike,
): {
  wizardSteps: AviationWizardStep[];
  primaryActivityFamilyOptions: typeof PRIMARY_ACTIVITY_FAMILY_OPTIONS;
  primaryActivityFamilyBlurbs: typeof PRIMARY_ACTIVITY_FAMILY_BLURBS;
  refinedProfileOptionsByFamily: typeof REFINED_PROFILE_OPTIONS_BY_FAMILY;
  scopeMultiOptions: typeof AVIATION_SCOPE_MULTI_OPTIONS;
  riskTargetOptions: typeof AVIATION_RISK_TARGET_OPTIONS;
  defaultProfileSlugByFamily: typeof DEFAULT_PROFILE_SLUG_BY_FAMILY;
  familyToActivityDomains: typeof FAMILY_TO_ACTIVITY_DOMAINS;
} {
  const primaryActivityFamilyOptions = taxonomy.families.map((family) => ({
    value: family.value as PrimaryActivityFamily,
    label: family.label,
  })) as typeof PRIMARY_ACTIVITY_FAMILY_OPTIONS;

  const primaryActivityFamilyBlurbs = taxonomy.families.reduce(
    (acc, family) => {
      acc[family.value as PrimaryActivityFamily] = {
        summary: family.summary ?? family.label,
        detail: family.help_text ?? family.label,
      };
      return acc;
    },
    { ...PRIMARY_ACTIVITY_FAMILY_BLURBS },
  );

  const refinedProfileOptionsByFamily = Object.fromEntries(
    Object.entries(taxonomy.profiles_by_family).map(([family, profiles]) => [
      family,
      profiles.map(([slug, label]) => [slug, label] as const),
    ]),
  ) as unknown as typeof REFINED_PROFILE_OPTIONS_BY_FAMILY;

  return {
    wizardSteps: taxonomy.steps,
    primaryActivityFamilyOptions,
    primaryActivityFamilyBlurbs,
    refinedProfileOptionsByFamily,
    scopeMultiOptions: taxonomy.scope_options as typeof AVIATION_SCOPE_MULTI_OPTIONS,
    riskTargetOptions: taxonomy.risk_targets as typeof AVIATION_RISK_TARGET_OPTIONS,
    defaultProfileSlugByFamily: taxonomy.default_profile_slug_by_family as typeof DEFAULT_PROFILE_SLUG_BY_FAMILY,
    familyToActivityDomains: taxonomy.family_to_activity_domains as typeof FAMILY_TO_ACTIVITY_DOMAINS,
  };
}

export type AviationWizardStepId =
  | "activity_family"
  | "authority_profile"
  | "scope_assets"
  | "facility_location";

export interface AviationWizardStep {
  id: AviationWizardStepId;
  title: string;
  description: string;
}

/** Screen 3 — grouped checkbox options (legacy keys; aligns with Rules prompts). */
export const AVIATION_SCOPE_MULTI_OPTIONS = {
  operation_contexts: [
    ["in_flight", "On board / in flight"],
    ["dispatch", "Operational control / dispatch"],
    ["maintenance_hangar", "Maintenance hangar / repair station"],
    ["airport_movement_area", "Airport movement area"],
    ["ramp_fueling_area", "Ramp / fueling area"],
    ["terminal_sterile_area", "Terminal / sterile area"],
    ["cargo_facility", "Cargo facility"],
    ["uas_launch_recovery", "UAS launch / recovery"],
  ],
  asset_types: [
    ["aircraft", "Aircraft"],
    ["powered_lift", "Powered-lift"],
    ["uas", "UAS"],
    ["engine", "Engine"],
    ["propeller", "Propeller"],
    ["component", "Appliance / component"],
    ["airport_certificate_manual", "Airport certificate / manual"],
    ["runway_taxiway_ramp", "Runway / taxiway / ramp"],
    ["security_credential", "Security area / credential"],
  ],
  certificates_or_approvals: [
    ["part_119_certificate", "FAA Part 119 certificate"],
    ["operations_specifications", "Operations specifications"],
    ["repair_station_certificate", "Repair station certificate"],
    ["airport_operating_certificate", "Airport operating certificate"],
    ["stc", "STC"],
    ["pma_tsoa", "PMA / TSOA"],
    ["remote_pilot_certificate", "Remote pilot certificate"],
    ["tsa_security_program", "TSA security program"],
    ["dot_economic_authority", "DOT economic authority"],
  ],
} as const;

/** Screen 4 — monitoring priorities (extended vs legacy wizard). */
export const AVIATION_RISK_TARGET_OPTIONS: readonly (readonly [string, string])[] = [
  ["civil_penalties", "Civil monetary penalties"],
  ["certificate_action", "Certificate suspension / revocation"],
  ["failed_audit", "Failed inspection / audit"],
  ["ad_noncompliance", "AD noncompliance"],
  ["operating_without_authority", "Operating without authority"],
  ["recordkeeping_violation", "Recordkeeping violations"],
  ["missed_training", "Missed renewal / recurrent training"],
  ["consumer_complaint", "Consumer / passenger complaints"],
  ["tsa_security_violation", "TSA security directives / violations"],
  ["hazmat_cargo_violation", "Hazmat / cargo violations"],
  ["nprm_rule_monitoring", "NPRM / proposed rule monitoring"],
  ["final_rule_cfr_amendment", "Final rule / CFR amendment"],
  ["manual_program_amendment", "Manual / program amendment deadlines"],
  ["faa_order_ac_saib_watch", "FAA Order / AC / SAIB watchlist"],
];

export const AVIATION_OPTION_HELP_TEXT: Record<string, string> = {
  aviation_air_carrier_part_121:
    "14 CFR Parts 119 and 121 — scheduled common carriage. Certificate holder operates under an FAA air carrier certificate with Operations Specifications; flight standards oversight includes manuals, training programs, MEL/MEL-style approvals where applicable, and continued operational safety programs.",
  aviation_air_operator_part_135:
    "14 CFR Parts 119 and 135 — commuter and on-demand operations (charter, air taxi, some scheduled). Operational control, pilot qualifications, maintenance arrangements, and certificate limitations live in your ops specs and manuals — different enforcement rhythm than Part 121.",
  aviation_general_operations_part_91:
    "14 CFR Part 91 — general operating and flight rules for most civil aircraft in the NAS. Often layered with Part 61/135/121 when commercial; pick this baseline when your primary compliance story is GA-style ops, training, or non-scheduled flying without a Part 121 certificate.",
  aviation_fractional_part_91k:
    "14 CFR Part 91, Subpart K — fractional ownership programs: program manager duties, managed aircraft rules, dry-lease structures, and DO-style controls that differ from classic charter.",
  aviation_large_civil_aircraft_part_125:
    "14 CFR Part 125 — certain large airplane operations that are not Part 121 or 135 (with specific eligibility). Certificate, manual, and operational requirements differ from commuter/on-demand packages.",
  aviation_foreign_air_carrier_part_129:
    "14 CFR Part 129 — foreign air carriers and operators serving the U.S.; compliance bridges FAA requirements with home-state oversight (OpsSpecs / authorization style differs from domestic 121).",
  aviation_air_tour_part_136:
    "14 CFR Part 136 — commercial air tours and certain door-off / low-altitude operating constraints where applicable; distinct from generic Part 135 charter when tour rules drive your program.",
  aviation_airport_part_139:
    "14 CFR Part 139 — airports required to hold an Airport Operating Certificate: Airport Certification Manual (ACM), movement-area safety, ARFF index, self-inspections, fuel safety, Part 139 inspections, and FAA surveillance.",
  aviation_airport_public_use_non_139:
    "Public-use airports that are not Part 139-certificated still face FAA airport standards, NOTAM responsibilities, grant-assurance obligations if federal funds apply, and often state/local safety rules.",
  aviation_airport_sponsor_grant_assurance:
    "Airport sponsor obligations under FAA grant assurances (when AIP funds used): maintain airport as planned, revenue use, compatible land use, reporting — distinct from only holding a Part 139 certificate.",
  aviation_vertiport_heliport_operations:
    "Heliport / vertiport operations — landing sites, marking/lighting, approaches, and emerging vertiport guidance; often blends Part 139-style safety practices with rotorcraft operating rules.",
  aviation_repair_station_part_145:
    "14 CFR Part 145 — domestic repair station certificate: ratings, capability list, repair station manual, quality system, and FAA repair station surveillance.",
  aviation_foreign_repair_station_part_145:
    "14 CFR Part 145 — foreign repair station certification and FAA interface (including specialty ratings / limitations) differs from a domestic station even though the “145” family is shared.",
  aviation_air_carrier_maintenance_121_135:
    "Maintenance performed under an air carrier’s Parts 121/135 certificate and maintenance program (often continuous airworthiness + OpSpecs / manual controlled) rather than as a standalone Part 145 certificate holder.",
  aviation_type_certification_part_21:
    "14 CFR Part 21 — type certification path for new aircraft / articles; coordination with FAA on design, flight test, compliance findings, and TC issuance.",
  aviation_stc_part_21_subpart_e: "14 CFR Part 21, Subpart E — supplemental type certificates for modifications to type-certificated products.",
  aviation_pma_part_21:
    "14 CFR Part 21, Subparts K/G-style PMA pathways — approved replacement / modification articles produced under FAA production oversight.",
  aviation_tso_authorization_part_21:
    "14 CFR Part 21, Subpart O — technical standard order authorization for articles meeting TSO standards.",
  aviation_production_approval_part_21:
    "14 CFR Part 21 production approvals (e.g., PC) — manufacturing under approved design data with FAA surveillance.",
  aviation_oda_der_dar_certification_support:
    "ODA / DER / DAR / DMIR roles — delegated certification and conformity paths under FAA oversight; distinct compliance cadence from pure manufacturing or pure operator worlds.",
  aviation_uas_part_107:
    "14 CFR Part 107 — small unmanned aircraft operations; often paired with 14 CFR Part 89 remote identification and waiver/exemption packages when you operate beyond vanilla rules.",
  aviation_uas_bvlos_waiver_exemption:
    "Beyond-visual-line-of-sight or non-standard UAS ops typically driven by waivers/exemptions and letters of authorization — compliance attaches to those instruments plus Parts 107/89.",
  aviation_uas_remote_id_part_89:
    "14 CFR Part 89 — remote identification equipage and operating requirements for unmanned aircraft in the NAS.",
  aviation_agricultural_part_137: "14 CFR Part 137 — agricultural aircraft operations (dispensing, low-altitude patterns, work-area controls).",
  aviation_external_load_part_133:
    "14 CFR Part 133 — rotorcraft external-load operations; hooks, briefings, and operating limits differ materially from standard Part 135 passenger carriage.",
  aviation_airport_security_tsa_1542: "49 CFR Part 1542 — airport operator security program and regulated areas.",
  aviation_aircraft_operator_security_tsa_1544:
    "49 CFR Part 1544 — aircraft operator security programs (including certain charter / scheduled contexts): manuals, training, and security directives.",
  aviation_hazmat_air_transport:
    "Hazardous materials / dangerous goods moved by air — 49 CFR (including Part 175), ICAO/IATA-style segregation and documentation expectations.",
  aviation_cargo_handling_security:
    "Cargo screening / chain-of-custody and ground-handling security interfaces — often blends TSA cargo rules with airport and operator programs.",
  aviation_public_charter_part_380:
    "14 CFR Part 380 — DOT public charter rules: filings, disclosures, escrow / participant protections depending on your charter type.",
  aviation_air_charter_broker:
    "Charter broker / arranger economics — DOT/FAA rules on advertising, disclosure, and “single entity” concepts vary by how you sell or arrange lift.",
  aviation_ticket_agent_advertising:
    "Ticket-agent style duties and advertising representations under DOT economic regulations — distinct from holding an operating certificate.",
  aviation_dot_consumer_protection:
    "Broader DOT aviation consumer rules (e.g., disability, tarmac, advertising) when your exposure is mostly consumer-facing obligations.",
  aircraft_operations:
    "Commercial flying and flight operations for hire or compensation — typically Parts 91, 119, 121, 135, and related maintenance and airworthiness duties tied to the operating certificate.",
  airport_operations:
    "Airport roles and airport certificate obligations — 14 CFR Part 139 (when certificated), grant assurances, movement areas, and public-use airport responsibilities.",
  maintenance_repair_alteration:
    "Maintenance, preventive maintenance, alteration, and inspection rules — 14 CFR Parts 43, 65, and 145 when repair stations or approved programs apply.",
  design_certification_manufacturing:
    "Type certification, production, and approval-holder obligations — 14 CFR Part 21 and delegated conformity pathways.",
  uas_aam:
    "Unmanned aircraft and emerging advanced air mobility — 14 CFR Parts 89 (Remote ID), 107 (small UAS), plus waivers/exemptions when you operate outside baseline rules.",
  security:
    "Aviation security programs — 49 CFR Parts 1540, 1542 (airport / sterile areas), and 1544 (operator security programs) depending on your role.",
  hazmat_cargo:
    "Dangerous goods by air — 49 CFR Parts 171–180 plus ICAO/IATA-style segregation; Part 175 when shipped by air.",
  economic_consumer_arranger:
    "DOT charter / broker / ticketing and passenger-protection economics — 14 CFR Parts 259, 260, 380, and 399 as applicable.",
  in_flight:
    "Flight deck / cabin / duty-time contexts while the aircraft is operating — Parts 91, 121, or 135 operating rules and crew qualification obligations.",
  dispatch:
    "Operational control / dispatch — Part 121 Subpart T release authority or Part 135 operational control concepts depending on your certificate.",
  maintenance_hangar:
    "Hangars and shops where maintenance or alteration occurs — Parts 43 (documentation & approvals), 145 (repair station), or carrier maintenance programs.",
  airport_movement_area:
    "Runways, taxiways, and safety areas used for takeoff/landing and surface movement — Part 139 Subpart D on movement-area protection.",
  ramp_fueling_area:
    "Ramp operations and aircraft fueling — Part 139.321 fuel-storage/fire safety on certificate airports; plus NFPA/state/local fire prevention.",
  terminal_sterile_area:
    "Sterile and secured airport areas — 49 CFR Part 1542 access control, screening interfaces, and credentialing.",
  cargo_facility:
    "Cargo buildings and screening chain — TSA cargo programs (e.g., 1544 / 1548 context) and chain-of-custody expectations.",
  uas_launch_recovery:
    "Where unmanned aircraft launch, recover, or transition — Part 107 operating limitations and any waiver conditions.",
  aircraft:
    "Airframe / rotorcraft you operate, lease for operation, or maintain — ties to airworthiness, maintenance records, and operating rules.",
  powered_lift:
    "Powered-lift / eVTOL-type aircraft — emerging Part 194 and related certification concepts as operations mature.",
  uas:
    "Small unmanned aircraft systems — Parts 89 (Remote ID) and 107 (small UAS rule).",
  engine:
    "Engines — type certification (Part 33), overhaul limits, ADs, and maintenance records under Parts 43 and 145.",
  propeller:
    "Propellers — Part 35 conformity; maintenance and life limits under Parts 43 and 145.",
  component:
    "Articles / appliances — conformity under Parts 21 and 43; PMA or approved data when installing.",
  airport_certificate_manual:
    "Airport Certification Manual (ACM) — 14 CFR 139.203; describes how you comply with Part 139 at your airport.",
  runway_taxiway_ramp:
    "Pavement, markings, lighting, and obstacle clearing — Part 139 movement-area maintenance and inspection obligations.",
  security_credential:
    "Secure-area badges and credentialing — TSA / airport security programs for movement between sterile and non-sterile areas.",
  civil_penalties:
    "Civil monetary penalties — FAA/DOT/TSA schedules under 49 U.S.C. §46301 and agency enforcement policies; dollar-risk prioritization.",
  certificate_action:
    "Certificate suspension, revocation, or limitation exposure — 49 U.S.C. §44709 and Part 13 enforcement.",
  failed_audit:
    "Inspection findings, audits, and repeat discrepancies — FAA / TSA inspection reports and corrective-action tracking.",
  ad_noncompliance:
    "Airworthiness directives — 14 CFR §39.7 requires compliance before further flight after AD issuance.",
  operating_without_authority:
    "Operating as an air carrier or commercial operator without proper economic / operational authority — Part 119 certificate gaps.",
  recordkeeping_violation:
    "Maintenance records, pilot logs, duty records — 14 CFR §§43.9, 43.11 and crew-recordkeeping rules.",
  missed_training:
    "Recurrent training and currency — Parts 61, 65, 121, and 135 training programs depending on role.",
  consumer_complaint:
    "DOT / passenger complaints — Parts 259, 260, 382 (disability), and 399-style consumer obligations.",
  tsa_security_violation:
    "Security directives and emergency amendments — TSA enforcement tied to 49 CFR Parts 1540, 1542, and 1544.",
  hazmat_cargo_violation:
    "Undeclared hazmat, segregation errors, or shipper violations — 49 CFR Part 175 and PHMSA enforcement.",
  nprm_rule_monitoring:
    "Federal Register NPRMs — comment deadlines, policy shifts, and implementation planning before rules finalize.",
  final_rule_cfr_amendment:
    "Final rules — binding CFR amendments once effective; OpSpecs / manual updates often follow.",
  manual_program_amendment:
    "Manual and OpSpec amendment deadlines — certificate holder submissions after rule or oversight changes.",
  faa_order_ac_saib_watch:
    "FAA Orders, Advisory Circulars, SAIBs — guidance until incorporated into your FAA-approved program.",
  part_119_certificate:
    "14 CFR Part 119 — certificate management for carriers and commercial operators before carrying people/cargo for compensation or hire.",
  operations_specifications:
    "Operations Specifications — 14 CFR 119.49; operational limits, authorizations, and variations tied to your FAA certificate.",
  repair_station_certificate:
    "14 CFR Part 145 repair station — ratings, capability list, repair station manual, and FAA surveillance as a certificate holder.",
  airport_operating_certificate:
    "14 CFR Part 139 Airport Operating Certificate — issued to airports that meet FAR Part 139 Index criteria for scheduled air carrier operations (and certain other cases). The certificate holder maintains an Airport Certification Manual (ACM), conducts self-inspections, meets movement-area standards (runways, taxiways, safety areas), maintains ARFF to the required index, complies with fuel farm inspections and safety under Subpart A, and is subject to FAA Part 139 inspections and oversight. Check this when your entity holds or operates under that airport certificate — not when you only lease a ticket counter.",
  stc: "14 CFR Part 21, Subpart E — supplemental type certificate modifying a type-certificated product.",
  pma_tsoa: "14 CFR Part 21 — PMA articles (Subpart K) or TSO authorization (Subpart O) pathways.",
  remote_pilot_certificate:
    "14 CFR Part 107, Subpart C — remote pilot certificate; recurrent requirements and operating limits for small UAS.",
  tsa_security_program:
    "49 CFR Parts 1542 or 1544 — TSA-approved security program: manuals, training, and compliance with security directives.",
  dot_economic_authority:
    "DOT economic authority — 14 CFR Parts 201, 204, 298, and 380 for charters, brokers, and ticket agents as applicable.",
};

export const AVIATION_WIZARD_STEPS: AviationWizardStep[] = [
  {
    id: "activity_family",
    title: "What aviation activity should UniComply monitor first?",
    description:
      "Start with the lane that matters most day-to-day — flying passengers or cargo, running an airport, maintaining aircraft, building parts, training crews, drones, security/hazmat, or selling travel. You can add extra regulated functions below (many organizations wear more than one hat). Hover each row for CFR-flavored context.",
  },
  {
    id: "authority_profile",
    title: "Which certificate or regulatory identity fits you best?",
    description:
      "You’re doing great—this step tells UniComply which FAA/TSA/DOT “lane” to use for rules, reminders, and risk signals. Select every certificate path or regulatory identity that applies (many sites wear more than one hat). You’ll see richer guidance on the next lines based on what you chose in Step 1!",
  },
  {
    id: "scope_assets",
    title: "What’s in scope — and what should we monitor?",
    description:
      "Tell us where your team actually works (flight deck, hangar, ramp, sterile terminal, cargo shed…) and which certificates or approvals you hold or rely on. Then pick what we should watch: fines, certificate actions, ADs, security issues, rule changes, and more. Every row has a short line plus hover detail — same helpful pattern on every checkbox.",
  },
  {
    id: "facility_location",
    title: "Where is this regulated entity?",
    description:
      "We use the name and place you use in real life to match FAA data (e.g. NASR for airports), improve search, and tie enforcement geography. It’s the same friendly title + summary + longer hover on each field — nothing cryptic, we promise.",
  },
];

/** Short line under wizard checkbox labels; hover shows full detail from AVIATION_OPTION_HELP_TEXT */
export const AVIATION_WIZARD_SUMMARIES: Partial<Record<string, string>> = {
  in_flight: "In-flight ops — crew duties, altitudes, and operating rules while airborne.",
  dispatch: "Dispatch / release — who exercises operational control before takeoff.",
  maintenance_hangar: "Hangar & shop work — maintenance, alteration, and documentation.",
  airport_movement_area: "Movement areas — runways, taxiways, and protected surfaces.",
  ramp_fueling_area: "Ramp & fueling — aircraft servicing, spill prevention, and fire safety.",
  terminal_sterile_area: "Sterile side — TSA-controlled access and screening interfaces.",
  cargo_facility: "Cargo buildings — screening, chain of custody, and chain-of-security.",
  uas_launch_recovery: "Drone ops area — launch, recovery, and Part 107 footprint.",
  aircraft: "Whole aircraft — operation, leaseback, or maintenance responsibility.",
  powered_lift: "Powered-lift / eVTOL — new rule sets alongside type certification.",
  uas: "Small UAS — Remote ID plus Part 107 operating limits.",
  engine: "Engines — overhaul, ADs, and life-limited parts.",
  propeller: "Propellers — dynamic balance, life limits, and records.",
  component: "Articles & appliances — approved data and installation rules.",
  airport_certificate_manual: "ACM — how your airport complies with Part 139 in writing.",
  runway_taxiway_ramp: "Pavement & lighting — friction, markings, and obstacle free space.",
  security_credential: "Badging — secure-area access and credential compliance.",
  part_119_certificate:
    "Part 119 certificate — economic authority gate before carrying people/cargo for hire.",
  operations_specifications:
    "OpsSpecs — what FAA authorized for your certificate (routes, airports, limits).",
  repair_station_certificate:
    "Part 145 certificate — ratings, capability list, and repair station manual.",
  airport_operating_certificate:
    "Part 139 Airport Operating Certificate — ACM, movement safety, ARFF index, inspections.",
  stc: "STC — approved modification to a type-certificated aircraft or article.",
  pma_tsoa: "PMA / TSOA — production approval for articles under Part 21.",
  remote_pilot_certificate: "Remote pilot — Part 107 certificate and recency.",
  tsa_security_program: "TSA program — security plan, training, and directive compliance.",
  dot_economic_authority: "DOT economics — charters, brokers, ticketing, filings.",
  civil_penalties: "Civil penalties — FAA/DOT/TSA fine exposure and schedules.",
  certificate_action: "Certificate action — suspension, revocation, or limitation risk.",
  failed_audit: "Inspection outcomes — repeat findings and corrective actions.",
  ad_noncompliance: "Airworthiness directives — compliance before further flight.",
  operating_without_authority: "Authority gaps — flying or selling without proper certificates.",
  recordkeeping_violation: "Records — logbooks, maintenance releases, duty times.",
  missed_training: "Training currency — pilot, mechanic, or security training lapses.",
  consumer_complaint: "Consumer rules — DOT complaints and passenger-protection duties.",
  tsa_security_violation: "Security enforcement — SDs and emergency amendment compliance.",
  hazmat_cargo_violation: "Hazmat — undeclared DG, segregation, or shipper errors.",
  nprm_rule_monitoring: "NPRMs — proposed rules, comments, and adoption planning.",
  final_rule_cfr_amendment: "Final rules — effective dates and CFR revisions.",
  manual_program_amendment: "Manual deadlines — revisions after rule or oversight changes.",
  faa_order_ac_saib_watch: "Guidance stack — Orders, ACs, SAIBs vs your approved program.",
  aviation_air_carrier_part_121: "Part 121 — scheduled airline / air carrier certificate.",
  aviation_air_operator_part_135: "Part 135 — charter, commuter, on-demand certificate.",
  aviation_general_operations_part_91: "Part 91 — general operating rules (often GA baseline).",
  aviation_fractional_part_91k: "Part 91 subpart K — fractional ownership program manager.",
  aviation_large_civil_aircraft_part_125: "Part 125 — large airplane ops outside 121/135.",
  aviation_foreign_air_carrier_part_129: "Part 129 — foreign carrier serving the U.S.",
  aviation_air_tour_part_136: "Part 136 — commercial air tours / defined operating rules.",
  aviation_airport_part_139: "Part 139 — certificated airport with Airport Operating Certificate.",
  aviation_airport_public_use_non_139: "Public-use — not Part 139-certificated; still FAA obligations.",
  aviation_airport_sponsor_grant_assurance: "Sponsor — grant assurances & federal funding duties.",
  aviation_vertiport_heliport_operations: "Heliport / vertiport — rotorcraft sites & markings.",
  aviation_repair_station_part_145: "Domestic Part 145 — FAA repair station certificate.",
  aviation_foreign_repair_station_part_145: "Foreign Part 145 — overseas repair station interface.",
  aviation_air_carrier_maintenance_121_135: "Carrier maintenance — under 121/135 program vs standalone 145.",
  aviation_type_certification_part_21: "Type certification — new aircraft type under Part 21.",
  aviation_stc_part_21_subpart_e: "STC — supplemental type certificate modifications.",
  aviation_pma_part_21: "PMA — approved replacement/modification article production.",
  aviation_tso_authorization_part_21: "TSOA — technical standard order authorization.",
  aviation_production_approval_part_21: "Production approval — PC-style manufacturing oversight.",
  aviation_oda_der_dar_certification_support: "Delegation — ODA/DER/DAR conformity support.",
  aviation_uas_part_107: "Part 107 — small unmanned aircraft baseline operations.",
  aviation_uas_bvlos_waiver_exemption: "Waivers — BVLOS / non-standard UAS approvals.",
  aviation_uas_remote_id_part_89: "Part 89 — Remote ID equipage and operating rules.",
  aviation_agricultural_part_137: "Part 137 — agricultural spraying / dispensing ops.",
  aviation_external_load_part_133: "Part 133 — rotorcraft external-load hooks & briefings.",
  aviation_airport_security_tsa_1542: "Part 1542 — airport operator security program.",
  aviation_aircraft_operator_security_tsa_1544: "Part 1544 — aircraft operator security program.",
  aviation_hazmat_air_transport: "Hazmat air — dangerous goods documentation & segregation.",
  aviation_cargo_handling_security: "Cargo security — screening and ground-handling chain.",
  aviation_public_charter_part_380: "Part 380 — DOT public charter filings & disclosures.",
  aviation_air_charter_broker: "Broker / arranger — how you sell or arrange lift.",
  aviation_ticket_agent_advertising: "Ticket agent — advertising & disclosure obligations.",
  aviation_dot_consumer_protection: "DOT consumer — disability, tarmac, and complaint rules.",
};

/** Screen 4 field blurbs (shown next to labels + hover detail). */
export const AVIATION_LOCATION_FIELD_BLURBS = {
  facility_name: {
    summary:
      "Type to search live — legal name, DBA, or certificate holder name (inspector-ready spelling).",
    detail:
      "On Add Facility, the name box runs a live lookup as you type (FAA / resolver feeds), with state and city helping disambiguate. Use the same spelling you use with regulators. Hover anytime for this reminder.",
  },
  state: {
    summary: "Two-letter state narrows duplicate names (SEA vs Spokane).",
    detail:
      "Airports and operators often share brands across states; a state code cuts ambiguity and helps state-specific enforcement or grant snapshots. Optional but recommended when the name is common.",
  },
  city: {
    summary: "City filters noisy matches when the same brand appears nationwide.",
    detail:
      "Optional. If nothing turns up, we can retry without city (mobile ops, new sites). Same helpful pattern: summary here, full context on hover.",
  },
} as const;

export function getWizardOptionBlurb(optionKey: string, optionLabel: string): { summary: string; detail: string } {
  const detail =
    AVIATION_OPTION_HELP_TEXT[optionKey] ??
    `Regulatory scope for “${optionLabel}” follows from your profile and certificate choices.`;
  const summary =
    AVIATION_WIZARD_SUMMARIES[optionKey] ??
    (optionLabel.length > 90 ? `${optionLabel.slice(0, 87)}…` : `${optionLabel}.`);
  return { summary, detail };
}
